import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PendingItems = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overdue');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Declare missing variables that are used in the component
  const userProfile = null; // placeholder - should be provided by context/props
  const supabase = null; // placeholder - should be provided by context/props
  
  // Filter states
  const [filters, setFilters] = useState({
    priority: [],
    itemType: [],
    amountRange: { min: '', max: '' },
    dateRange: { from: '', to: '' },
    searchQuery: ''
  });
  
  const [appliedFilters, setAppliedFilters] = useState({
    priority: [],
    itemType: [],
    amountRange: { min: '', max: '' },
    dateRange: { from: '', to: '' },
    searchQuery: ''
  });

  // Load real pending items from Supabase instead of using dummy data
  const [pendingItems, setPendingItems] = useState({
    overdue: [],
    upcoming: [],
    approvals: []
  });

  const [loading, setLoading] = useState(true);
  
  // Load real pending items from database
  useEffect(() => {
    const loadPendingItems = async () => {
      if (!userProfile?.organization_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Load overdue invoices
        const { data: overdueInvoices } = await supabase
          ?.from('invoices')
          ?.select('id, invoice_number, customer_name, total_amount, due_date, status')
          ?.eq('organization_id', userProfile?.organization_id)
          ?.in('status', ['sent', 'viewed'])
          ?.lt('due_date', new Date()?.toISOString())
          ?.limit(10);

        // Load upcoming bills
        const { data: upcomingBills } = await supabase
          ?.from('bills')
          ?.select('id, bill_number, vendor_name, total_amount, due_date, status')
          ?.eq('organization_id', userProfile?.organization_id)
          ?.eq('status', 'approved')
          ?.gte('due_date', new Date()?.toISOString())
          ?.limit(10);

        // Transform data to match component structure
        const overdueItems = overdueInvoices?.map(invoice => ({
          id: invoice?.invoice_number || invoice?.id,
          type: 'invoice',
          title: `Invoice #${invoice?.invoice_number}`,
          customer: invoice?.customer_name || 'Unknown Customer',
          amount: invoice?.total_amount || 0,
          dueDate: invoice?.due_date,
          daysOverdue: Math?.ceil((new Date() - new Date(invoice?.due_date)) / (1000 * 60 * 60 * 24)),
          priority: 'high'
        })) || [];

        const upcomingItems = upcomingBills?.map(bill => ({
          id: bill?.bill_number || bill?.id,
          type: 'bill',
          title: bill?.bill_number ? `Bill #${bill?.bill_number}` : 'Expense Bill',
          vendor: bill?.vendor_name || 'Unknown Vendor',
          amount: bill?.total_amount || 0,
          dueDate: bill?.due_date,
          daysUntilDue: Math?.ceil((new Date(bill?.due_date) - new Date()) / (1000 * 60 * 60 * 24)),
          priority: 'medium'
        })) || [];

        setPendingItems({
          overdue: overdueItems,
          upcoming: upcomingItems,
          approvals: [] // Could be populated with expense approvals if that feature exists
        });

      } catch (error) {
        console.error('Error loading pending items:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPendingItems();
  }, [userProfile?.organization_id]);

  // Filter items based on applied filters
  const getFilteredItems = () => {
    const items = pendingItems?.[activeTab] || [];
    
    return items?.filter(item => {
      // Priority filter
      if (appliedFilters?.priority?.length > 0 && !appliedFilters?.priority?.includes(item?.priority)) {
        return false;
      }
      
      // Item type filter
      if (appliedFilters?.itemType?.length > 0 && !appliedFilters?.itemType?.includes(item?.type)) {
        return false;
      }
      
      // Amount range filter
      if (appliedFilters?.amountRange?.min && item?.amount < parseFloat(appliedFilters?.amountRange?.min)) {
        return false;
      }
      if (appliedFilters?.amountRange?.max && item?.amount > parseFloat(appliedFilters?.amountRange?.max)) {
        return false;
      }
      
      // Date range filter
      if (appliedFilters?.dateRange?.from || appliedFilters?.dateRange?.to) {
        const itemDate = activeTab === 'approvals' ? 
          new Date(item?.submittedDate) : new Date(item?.dueDate);
        
        if (appliedFilters?.dateRange?.from && itemDate < new Date(appliedFilters?.dateRange?.from)) {
          return false;
        }
        if (appliedFilters?.dateRange?.to && itemDate > new Date(appliedFilters?.dateRange?.to)) {
          return false;
        }
      }
      
      // Search query filter
      if (appliedFilters?.searchQuery) {
        const query = appliedFilters?.searchQuery?.toLowerCase();
        const searchableText = [
          item?.title,
          item?.customer,
          item?.vendor,
          item?.employee,
          item?.department,
          item?.id
        ]?.filter(Boolean)?.join(' ')?.toLowerCase();
        
        if (!searchableText?.includes(query)) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Add this line to define filteredItems
  const filteredItems = getFilteredItems();

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleArrayFilterChange = (filterType, value) => {
    setFilters(prev => {
      const currentArray = prev?.[filterType] || [];
      const newArray = currentArray?.includes(value)
        ? currentArray?.filter(item => item !== value)
        : [...currentArray, value];
      
      return {
        ...prev,
        [filterType]: newArray
      };
    });
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setShowFilterModal(false);
  };

  const clearFilters = () => {
    const emptyFilters = {
      priority: [],
      itemType: [],
      amountRange: { min: '', max: '' },
      dateRange: { from: '', to: '' },
      searchQuery: ''
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setShowFilterModal(false);
  };

  const hasActiveFilters = () => {
    return appliedFilters?.priority?.length > 0 ||
           appliedFilters?.itemType?.length > 0 ||
           appliedFilters?.amountRange?.min ||
           appliedFilters?.amountRange?.max ||
           appliedFilters?.dateRange?.from ||
           appliedFilters?.dateRange?.to ||
           appliedFilters?.searchQuery;
  };

  const getFilteredTabCount = (tabKey) => {
    const originalItems = pendingItems?.[tabKey] || [];
    if (!hasActiveFilters()) return originalItems?.length;
    
    const tempActiveTab = activeTab;
    // Temporarily set activeTab to calculate filtered count
    return originalItems?.filter(item => {
      if (appliedFilters?.priority?.length > 0 && !appliedFilters?.priority?.includes(item?.priority)) {
        return false;
      }
      if (appliedFilters?.itemType?.length > 0 && !appliedFilters?.itemType?.includes(item?.type)) {
        return false;
      }
      if (appliedFilters?.amountRange?.min && item?.amount < parseFloat(appliedFilters?.amountRange?.min)) {
        return false;
      }
      if (appliedFilters?.amountRange?.max && item?.amount > parseFloat(appliedFilters?.amountRange?.max)) {
        return false;
      }
      if (appliedFilters?.dateRange?.from || appliedFilters?.dateRange?.to) {
        const itemDate = tabKey === 'approvals' ? 
          new Date(item?.submittedDate) : new Date(item?.dueDate);
        
        if (appliedFilters?.dateRange?.from && itemDate < new Date(appliedFilters?.dateRange?.from)) {
          return false;
        }
        if (appliedFilters?.dateRange?.to && itemDate > new Date(appliedFilters?.dateRange?.to)) {
          return false;
        }
      }
      if (appliedFilters?.searchQuery) {
        const query = appliedFilters?.searchQuery?.toLowerCase();
        const searchableText = [
          item?.title,
          item?.customer,
          item?.vendor,
          item?.employee,
          item?.department,
          item?.id
        ]?.filter(Boolean)?.join(' ')?.toLowerCase();
        
        if (!searchableText?.includes(query)) {
          return false;
        }
      }
      return true;
    })?.length;
  };

  const getAvailableItemTypes = () => {
    const allItems = Object?.values(pendingItems)?.flat() || [];
    return [...new Set(allItems?.map(item => item?.type))];
  };

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const doc = new jsPDF();
      const currentDate = new Date()?.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      // PDF Header
      doc?.setFontSize(20);
      doc?.setTextColor(40, 40, 40);
      doc?.text('Pending Items Report', 20, 25);
      
      doc?.setFontSize(12);
      doc?.setTextColor(80, 80, 80);
      doc?.text(`Generated on: ${currentDate}`, 20, 35);
      doc?.text(`Category: ${activeTab?.charAt(0)?.toUpperCase() + activeTab?.slice(1)} Items`, 20, 45);

      const items = getFilteredItems();
      
      if (items?.length === 0) {
        doc?.text('No items found for this category.', 20, 65);
      } else {
        // Prepare table data
        const tableData = items?.map((item, index) => [
          index + 1,
          item?.id || '',
          item?.title || '',
          item?.customer || item?.vendor || item?.employee || item?.department || '',
          `$${item?.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
          activeTab === 'overdue' ? `${item?.daysOverdue} days overdue` :
          activeTab === 'upcoming' ? `Due in ${item?.daysUntilDue} days` :
          `Waiting ${item?.daysWaiting} days`,
          item?.priority?.charAt(0)?.toUpperCase() + item?.priority?.slice(1),
          activeTab === 'approvals' ? new Date(item?.submittedDate)?.toLocaleDateString('en-US')
            : new Date(item?.dueDate)?.toLocaleDateString('en-US')
        ]);

        // Table headers
        const headers = [
          '#', 'ID', 'Title', 'Entity', 'Amount', 'Status', 'Priority', 'Date'
        ];

        // Generate table
        doc?.autoTable({
          head: [headers],
          body: tableData,
          startY: 60,
          theme: 'striped',
          headStyles: {
            fillColor: [59, 130, 246],
            textColor: 255,
            fontStyle: 'bold'
          },
          styles: {
            fontSize: 9,
            cellPadding: 4
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: 15 },
            4: { halign: 'right' },
            6: { halign: 'center' },
            7: { halign: 'center' }
          }
        });

        // Summary section
        const finalY = doc?.lastAutoTable?.finalY + 20;
        doc?.setFontSize(14);
        doc?.setTextColor(40, 40, 40);
        doc?.text('Summary', 20, finalY);

        doc?.setFontSize(10);
        doc?.setTextColor(80, 80, 80);
        const totalAmount = items?.reduce((sum, item) => sum + (item?.amount || 0), 0);
        const highPriorityCount = items?.filter(item => item?.priority === 'high')?.length;

        doc?.text(`Total Items: ${items?.length}`, 20, finalY + 10);
        doc?.text(`Total Amount: $${totalAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 20, finalY + 20);
        doc?.text(`High Priority Items: ${highPriorityCount}`, 20, finalY + 30);
      }

      // Save PDF
      doc?.save(`pending-items-${activeTab}-${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
    setIsDownloading(false);
    setShowDownloadModal(false);
  };

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      const items = getFilteredItems();
      
      // Prepare Excel data
      const excelData = items?.map((item, index) => ({
        'S.No': index + 1,
        'ID': item?.id || '',
        'Title': item?.title || '',
        'Entity': item?.customer || item?.vendor || item?.employee || item?.department || '',
        'Amount': item?.amount || 0,
        'Amount (Formatted)': `$${item?.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        'Type': item?.type || '',
        'Priority': item?.priority?.charAt(0)?.toUpperCase() + item?.priority?.slice(1) || '',
        'Status': activeTab === 'overdue' ? `${item?.daysOverdue} days overdue` :
                 activeTab === 'upcoming' ? `Due in ${item?.daysUntilDue} days` :
                 `Waiting ${item?.daysWaiting} days`,
        'Date': activeTab === 'approvals' ? new Date(item?.submittedDate)?.toLocaleDateString('en-US')
          : new Date(item?.dueDate)?.toLocaleDateString('en-US'),
        'Due Date': activeTab !== 'approvals' ? new Date(item?.dueDate)?.toLocaleDateString('en-US') : '',
        'Submitted Date': activeTab === 'approvals' ? new Date(item?.submittedDate)?.toLocaleDateString('en-US') : ''
      }));

      // Create workbook
      const workbook = XLSX?.utils?.book_new();
      
      // Main data sheet
      const worksheet = XLSX?.utils?.json_to_sheet(excelData);
      
      // Auto-size columns
      const columnWidths = [
        { wch: 8 },   // S.No
        { wch: 15 },  // ID
        { wch: 25 },  // Title
        { wch: 20 },  // Entity
        { wch: 12 },  // Amount
        { wch: 15 },  // Amount Formatted
        { wch: 12 },  // Type
        { wch: 10 },  // Priority
        { wch: 18 },  // Status
        { wch: 12 },  // Date
        { wch: 12 },  // Due Date
        { wch: 12 }   // Submitted Date
      ];
      worksheet['!cols'] = columnWidths;

      XLSX?.utils?.book_append_sheet(workbook, worksheet, `${activeTab?.charAt(0)?.toUpperCase() + activeTab?.slice(1)} Items`);

      // Summary sheet
      const totalAmount = items?.reduce((sum, item) => sum + (item?.amount || 0), 0);
      const highPriorityCount = items?.filter(item => item?.priority === 'high')?.length;
      const mediumPriorityCount = items?.filter(item => item?.priority === 'medium')?.length;
      const lowPriorityCount = items?.filter(item => item?.priority === 'low')?.length;

      const summaryData = [
        { 'Metric': 'Report Type', 'Value': `${activeTab?.charAt(0)?.toUpperCase() + activeTab?.slice(1)} Items` },
        { 'Metric': 'Generated On', 'Value': new Date()?.toLocaleDateString('en-US') },
        { 'Metric': 'Total Items', 'Value': items?.length },
        { 'Metric': 'Total Amount', 'Value': `$${totalAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}` },
        { 'Metric': 'High Priority Items', 'Value': highPriorityCount },
        { 'Metric': 'Medium Priority Items', 'Value': mediumPriorityCount },
        { 'Metric': 'Low Priority Items', 'Value': lowPriorityCount }
      ];

      const summarySheet = XLSX?.utils?.json_to_sheet(summaryData);
      summarySheet['!cols'] = [{ wch: 20 }, { wch: 25 }];
      XLSX?.utils?.book_append_sheet(workbook, summarySheet, 'Summary');

      // Save Excel file
      const excelBuffer = XLSX?.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `pending-items-${activeTab}-${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Error generating Excel file. Please try again.');
    }
    setIsDownloading(false);
    setShowDownloadModal(false);
  };

  const handleViewAllClick = () => {
    switch (activeTab) {
      case 'overdue':
        // Navigate to sales hub for overdue invoices or purchase center for bills
        const overdueHasInvoices = pendingItems?.overdue?.some(item => item?.type === 'invoice');
        navigate(overdueHasInvoices ? '/sales-hub' : '/purchase-center');
        break;
      case 'upcoming': navigate('/purchase-center');
        break;
      case 'approvals': navigate('/purchase-center');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error bg-error/10 border-error/20';
      case 'medium': return 'text-warning bg-warning/10 border-warning/20';
      case 'low': return 'text-success bg-success/10 border-success/20';
      default: return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'invoice': return 'FileText';
      case 'bill': return 'FileX';
      case 'expense': return 'Receipt';
      case 'purchase_order': return 'ShoppingCart';
      default: return 'AlertCircle';
    }
  };

  const handleItemClick = (item) => {
    switch (item?.type) {
      case 'invoice': navigate('/sales-hub');
        break;
      case 'bill': case'expense': case'purchase_order': navigate('/purchase-center');
        break;
      default:
        navigate('/dashboard');
    }
  };

  // Update the getTabCount function to use filtered count
  const getTabCount = (tabKey) => getFilteredTabCount(tabKey);

  const tabs = [
    { key: 'overdue', label: 'Overdue', icon: 'AlertTriangle' },
    { key: 'upcoming', label: 'Upcoming', icon: 'Clock' },
    { key: 'approvals', label: 'Approvals', icon: 'CheckCircle' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-professional">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Pending Items</h2>
          <div className="flex items-center space-x-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowDownloadModal(true)}
              disabled={!filteredItems?.length}
            >
              <Icon name="Download" size={16} />
              Download
            </Button>
            <Button 
              variant={hasActiveFilters() ? "default" : "ghost"}
              size="sm"
              onClick={() => setShowFilterModal(true)}
              className={hasActiveFilters() ? "bg-primary/10 text-primary hover:bg-primary/20" : ""}
            >
              <Icon name="Filter" size={16} />
              Filter
              {hasActiveFilters() && (
                <span className="ml-1 px-1.5 py-0.5 bg-primary text-primary-foreground rounded-full text-xs">
                  {Object?.values(appliedFilters)?.flat()?.filter(Boolean)?.length}
                </span>
              )}
            </Button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="mt-4">
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search pending items..."
              value={appliedFilters?.searchQuery}
              onChange={(e) => {
                const newFilters = { ...appliedFilters, searchQuery: e?.target?.value };
                setAppliedFilters(newFilters);
                setFilters(newFilters);
              }}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters() && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Active filters:</span>
            
            {appliedFilters?.priority?.map(priority => (
              <span key={priority} className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                Priority: {priority?.charAt(0)?.toUpperCase() + priority?.slice(1)}
                <button
                  onClick={() => {
                    const newPriority = appliedFilters?.priority?.filter(p => p !== priority);
                    const newFilters = { ...appliedFilters, priority: newPriority };
                    setAppliedFilters(newFilters);
                    setFilters(newFilters);
                  }}
                  className="ml-1 hover:text-primary/70"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            ))}
            
            {appliedFilters?.itemType?.map(type => (
              <span key={type} className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                Type: {type?.charAt(0)?.toUpperCase() + type?.slice(1)}
                <button
                  onClick={() => {
                    const newTypes = appliedFilters?.itemType?.filter(t => t !== type);
                    const newFilters = { ...appliedFilters, itemType: newTypes };
                    setAppliedFilters(newFilters);
                    setFilters(newFilters);
                  }}
                  className="ml-1 hover:text-primary/70"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            ))}
            
            {(appliedFilters?.amountRange?.min || appliedFilters?.amountRange?.max) && (
              <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                Amount: ${appliedFilters?.amountRange?.min || '0'} - ${appliedFilters?.amountRange?.max || '∞'}
                <button
                  onClick={() => {
                    const newFilters = { ...appliedFilters, amountRange: { min: '', max: '' } };
                    setAppliedFilters(newFilters);
                    setFilters(newFilters);
                  }}
                  className="ml-1 hover:text-primary/70"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear all
            </Button>
          </div>
        )}
      </div>
      
      <div className="border-b border-border">
        <div className="flex space-x-0">
          {tabs?.map((tab) => (
            <button
              key={tab?.key}
              onClick={() => setActiveTab(tab?.key)}
              className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-150 ${
                activeTab === tab?.key
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                activeTab === tab?.key ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {getTabCount(tab?.key)}
              </span>
            </button>
          ))}
        </div>
      </div>
      
      <div className="p-6">
        <div className="space-y-4">
          {filteredItems?.map((item) => (
            <div
              key={item?.id}
              onClick={() => handleItemClick(item)}
              className="p-4 bg-muted/20 hover:bg-muted/40 rounded-lg border border-border hover:border-primary/20 cursor-pointer transition-all duration-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-muted/50 rounded-lg flex items-center justify-center">
                    <Icon name={getItemIcon(item?.type)} size={18} className="text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">{item?.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {item?.customer || item?.vendor || item?.employee || item?.department}
                    </p>
                    <div className="flex items-center space-x-4 mt-2">
                      {activeTab === 'overdue' && (
                        <span className="text-xs text-error font-medium">
                          {item?.daysOverdue} days overdue
                        </span>
                      )}
                      {activeTab === 'upcoming' && (
                        <span className="text-xs text-warning font-medium">
                          Due in {item?.daysUntilDue} days
                        </span>
                      )}
                      {activeTab === 'approvals' && (
                        <span className="text-xs text-muted-foreground">
                          Waiting {item?.daysWaiting} days
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(item?.priority)}`}>
                        {item?.priority?.charAt(0)?.toUpperCase() + item?.priority?.slice(1)} Priority
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="font-semibold text-foreground">
                    ${item?.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {activeTab === 'approvals' ? new Date(item.submittedDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : new Date(item.dueDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredItems?.length === 0 && !loading && (
          <div className="text-center py-8">
            {hasActiveFilters() ? (
              <>
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-foreground mb-2">No items match your filters</h3>
                <p className="text-muted-foreground mb-4">Try adjusting your filters to see more results.</p>
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </>
            ) : (
              <>
                <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
                <h3 className="font-medium text-foreground mb-2">All caught up!</h3>
                <p className="text-muted-foreground mb-4">No {activeTab} items at the moment.</p>
                <div className="flex justify-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate('/sales-hub')}
                  >
                    Create Invoice
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate('/purchase-center')}
                  >
                    Add Bill
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {loading && (
          <div className="text-center py-8">
            <Icon name="Loader2" size={48} className="text-muted-foreground mx-auto mb-4 animate-spin" />
            <h3 className="font-medium text-foreground mb-2">Loading pending items...</h3>
            <p className="text-muted-foreground">Fetching data from your organization...</p>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {filteredItems?.length} {activeTab} items
              {hasActiveFilters() && ` (filtered from ${pendingItems?.[activeTab]?.length})`}
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleViewAllClick}
              className="hover:bg-primary/5 hover:text-primary hover:border-primary/20"
            >
              <Icon name="ExternalLink" size={14} />
              View All {activeTab?.charAt(0)?.toUpperCase() + activeTab?.slice(1)}
            </Button>
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg shadow-professional w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Filter Pending Items</h3>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Priority Filter */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">Priority</label>
                  <div className="space-y-2">
                    {['high', 'medium', 'low']?.map(priority => (
                      <label key={priority} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters?.priority?.includes(priority)}
                          onChange={() => handleArrayFilterChange('priority', priority)}
                          className="rounded border-border"
                        />
                        <span className="text-sm text-foreground capitalize">{priority} Priority</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Item Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">Item Type</label>
                  <div className="space-y-2">
                    {getAvailableItemTypes()?.map(type => (
                      <label key={type} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters?.itemType?.includes(type)}
                          onChange={() => handleArrayFilterChange('itemType', type)}
                          className="rounded border-border"
                        />
                        <span className="text-sm text-foreground capitalize">{type?.replace('_', ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Amount Range Filter */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">Amount Range</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Min amount"
                      value={filters?.amountRange?.min}
                      onChange={(e) => handleFilterChange('amountRange', { ...filters?.amountRange, min: e?.target?.value })}
                      className="px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                    <input
                      type="number"
                      placeholder="Max amount"
                      value={filters?.amountRange?.max}
                      onChange={(e) => handleFilterChange('amountRange', { ...filters?.amountRange, max: e?.target?.value })}
                      className="px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                </div>

                {/* Date Range Filter */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    {activeTab === 'approvals' ? 'Submitted Date Range' : 'Due Date Range'}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={filters?.dateRange?.from}
                      onChange={(e) => handleFilterChange('dateRange', { ...filters?.dateRange, from: e?.target?.value })}
                      className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                    <input
                      type="date"
                      value={filters?.dateRange?.to}
                      onChange={(e) => handleFilterChange('dateRange', { ...filters?.dateRange, to: e?.target?.value })}
                      className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-border">
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={clearFilters}>
                  Clear All Filters
                </Button>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowFilterModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={applyFilters}>
                    Apply Filters
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg shadow-professional w-full max-w-md mx-4">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Download Pending Items</h3>
                <button
                  onClick={() => setShowDownloadModal(false)}
                  className="text-muted-foreground hover:text-foreground"
                  disabled={isDownloading}
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-2">
                  Export {filteredItems?.length} {activeTab} items
                </p>
                <p className="text-xs text-muted-foreground">
                  Category: {activeTab?.charAt(0)?.toUpperCase() + activeTab?.slice(1)} Items
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleDownloadPDF}
                  disabled={isDownloading}
                  className="w-full justify-start"
                  variant="outline"
                >
                  <Icon name="FileText" size={16} className="text-red-600" />
                  <span className="ml-2">Download as PDF</span>
                  {isDownloading && <Icon name="Loader" size={14} className="ml-auto animate-spin" />}
                </Button>

                <Button
                  onClick={handleDownloadExcel}
                  disabled={isDownloading}
                  className="w-full justify-start"
                  variant="outline"
                >
                  <Icon name="FileSpreadsheet" size={16} className="text-green-600" />
                  <span className="ml-2">Download as Excel</span>
                  {isDownloading && <Icon name="Loader" size={14} className="ml-auto animate-spin" />}
                </Button>
              </div>

              <div className="mt-6 p-3 bg-muted/30 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <Icon name="Info" size={12} className="inline mr-1" />
                  Reports include item details, amounts, priorities, and summary statistics.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingItems;